/**
 * Block Table Component
 *
 * Displays a table of blocked users with search and pagination.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Trash2, Shield, ChevronLeft, ChevronRight, Search, X } from "lucide-react";
import type { Block } from "@/services/blockService";
import { formatDateTime } from "../utils/blockUtils";

interface BlockTableProps {
  blocks: Block[];
  loading: boolean;
  onUnblock: (block: Block) => void;
  onSearch?: (search: string) => void;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
  search?: string;
}

export function BlockTable({
  blocks,
  loading,
  onUnblock,
  onSearch,
  onNextPage,
  onPreviousPage,
  hasNext,
  hasPrevious,
  search,
}: BlockTableProps) {
  const [searchInput, setSearchInput] = React.useState(search || "");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSearch?.(searchInput);
    }
  };

  const handleClear = () => {
    setSearchInput("");
    onSearch?.("");
    inputRef.current?.focus();
  };

  const showPagination = hasNext || hasPrevious;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Daftar Block</CardTitle>
          <CardDescription>
            {blocks.length > 0 ? `Menampilkan ${blocks.length} blocks` : "Memuat data..."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (blocks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Daftar Block</CardTitle>
          <CardDescription>
            {search ? `Tidak ada hasil untuk "${search}"` : "Total: 0 blocks"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {onSearch && (
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    ref={inputRef}
                    placeholder="Cari berdasarkan callsign, id_client, atau nama... (tekan Enter)"
                    value={searchInput}
                    onChange={handleSearchChange}
                    onKeyDown={handleKeyDown}
                    className="pl-10 pr-10"
                  />
                  {searchInput && (
                    <button
                      onClick={handleClear}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Tidak ada data block</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daftar Block</CardTitle>
        <CardDescription>
          Menampilkan {blocks.length} block{blocks.length !== 1 ? "s" : ""}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Search Input */}
          {onSearch && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={inputRef}
                placeholder="Cari berdasarkan callsign, id_client, atau nama... (tekan Enter)"
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                className="pl-10 pr-10"
              />
              {searchInput && (
                <button
                  onClick={handleClear}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          {/* Table */}
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">ID Client / Callsign</TableHead>
                  <TableHead className="min-w-[200px]">Nama Pemilik</TableHead>
                  <TableHead className="min-w-[200px]">Area</TableHead>
                  <TableHead className="min-w-[200px]">Alasan</TableHead>
                  <TableHead className="min-w-[200px]">Keterangan</TableHead>
                  <TableHead className="min-w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {blocks.map((block) => (
                  <TableRow key={block._id}>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-sm">{block.id_client || "-"}</span>
                        <span className="font-mono text-xs text-muted-foreground">{block.callsign || "-"}</span>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {block.nama || "-"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className="text-sm">{block.province || "-"}</span>
                        <span className="text-xs text-muted-foreground">{block.kabkot || "-"}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {block.reason || "-"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      <div className="flex flex-col gap-1">
                        {block.blockedBy && <span>{block.blockedBy}</span>}
                        {block.blockedAt && <span className="text-xs">{formatDateTime(block.blockedAt)}</span>}
                        {!block.blockedBy && !block.blockedAt && <span>-</span>}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={() => onUnblock(block)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          {showPagination && (
            <div className="flex items-center justify-between gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={onPreviousPage}
                disabled={!hasPrevious || loading}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Sebelumnya
              </Button>
              <span className="text-sm text-muted-foreground">
                {blocks.length > 0 ? `Menampilkan ${blocks.length} items` : "Tidak ada data"}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={onNextPage}
                disabled={!hasNext || loading}
              >
                Berikutnya
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

